import { Text } from '@thanx/uikit/text'
import type { PhilosophyPosture } from 'api/recoveryAi.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import type { StyleObject } from 'styletron-react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import StanceBulletRow from './StanceBulletRow'
import {
  DEFAULT_RECOMMENDED_POSTURE,
  expectBulletKeys,
  STANCE_ORDER,
  wontBulletKeys,
} from './stances'

const t = buildTranslate('recovery_ai.wizard.philosophy')

// The slider runs LEAST→MOST generous, left→right — the reverse of STANCE_ORDER
// (which is the card stack's most→least display order). So ArrowRight / dragging
// right / a higher aria-valuenow all mean "more generous", matching the
// left=reserved → right=whatever-it-takes mental model. The 4 stops map 1:1 to
// the philosophy_posture enum (no backend/type change).
const SLIDER_ORDER: PhilosophyPosture[] = [...STANCE_ORDER].reverse()
const MAX_INDEX = SLIDER_ORDER.length - 1

// Position (%) of a stop along the track (SLIDER_ORDER is a fixed length-4
// constant, so MAX_INDEX is always 3 — no divide-by-zero guard needed).
const stopPercent = (index: number): number => (index / MAX_INDEX) * 100

// Horizontal placement for the RECOMMENDED FOR YOU badge. Centered on interior
// stops; flush-left on the first stop and flush-right on the last so the badge
// stays inside the track's bounds instead of overhanging the content column.
const recommendedBadgePosition = (
  index: number
): { left?: string; right?: string; transform?: string } => {
  if (index <= 0) return { left: '0' }
  if (index >= MAX_INDEX) return { right: '0' }
  return { left: `${stopPercent(index)}%`, transform: 'translateX(-50%)' }
}

// Pure pointer→stop math (exported for unit tests). Maps an absolute pointer x
// to the nearest discrete stop index along the track: x at the left edge → 0
// (least generous), x at the right edge → MAX_INDEX (most generous). Returns -1
// when the track has no measurable width yet — jsdom and a not-yet-laid-out
// track both report a 0-width rect, and bailing keeps a stray pointer event from
// dividing by zero or snapping to stop 0.
export const nearestStopIndex = (
  clientX: number,
  rect: { left: number; width: number } | undefined
): number => {
  if (!rect || rect.width === 0) return -1
  const ratio = (clientX - rect.left) / rect.width
  return Math.round(Math.min(1, Math.max(0, ratio)) * MAX_INDEX)
}

// Track thickness — the thin horizontal bar behind the stops that shows how far
// along the setting sits. 3× the original 4px per the R12 / D6 Figma; radius =
// height/2 keeps the pill ends. Shared body, so both the Settings pane and the
// Agent-Tuning modal pick up the thicker track.
const TRACK_HEIGHT = '12px'
const TRACK_RADIUS = '6px'

// Stop-dot diameters (px). AI-914: an unselected dot only 4px smaller than the
// selected one (the original 16px/20px pair) read as almost the same size,
// undermining the hierarchy — QA and the Figma reference both call for
// unselected dots flush with the track instead of bulging near-level with the
// thumb, so they reuse TRACK_HEIGHT rather than a size of their own. The
// selected stop stays proud of the track to read clearly as the thumb.
const STOP_SIZE_SELECTED = '20px'
const STOP_SIZE_DEFAULT = TRACK_HEIGHT

// AI-915: matches the Figma dev-mode spec exactly (node 2318:65821, pixel-
// sampled: bg #D6F5E4 = nephritis20, text #27AE60 = nephritis50) — replacing
// the old nephritis10 bg + grey90 text. NOTE: nephritis50-on-nephritis20 is
// only ~2.47:1, which fails WCAG AA (4.5:1) for this 10px text — the exact
// reason a prior fix (and StatusPill's "recommended" tone, StatusPill.tsx)
// used grey90 text instead. This intentionally reintroduces that AA failure
// to match Figma pixel-for-pixel per explicit request; flag to design if
// accessibility should win here instead. Exported (not inlined in the JSX
// below) so the colors are directly assertable in tests, mirroring
// StatusPill's `statusPillStyle`.
export const recommendedBadgeStyle: StyleObject = {
  whiteSpace: 'nowrap',
  backgroundColor: merchantTheme.colors.nephritis20,
  color: merchantTheme.colors.nephritis50,
  fontWeight: 700,
  fontSize: '10px',
  lineHeight: '12px',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  padding: '4px 8px',
  borderRadius: '4px',
}

type Props = {
  value: PhilosophyPosture
  onSelect: (posture: PhilosophyPosture) => void
  /**
   * Read-only mode (AI-64 CASL gate): the host has `access` but not `manage` on
   * RecoveryAI. The slider still renders the saved posture — only the edit paths
   * are severed (no keyboard, no pointer, no stop clicks). Defaults false so the
   * wizard and the Agent-Tuning modal call sites are unchanged.
   */
  disabled?: boolean
  /**
   * AI-1166: the merchant's actual posture-quiz recommendation
   * (`config.philosophy_recommended_posture`), driving where the "Recommended
   * for you" badge sits. Defaults to DEFAULT_RECOMMENDED_POSTURE so a host with
   * no per-merchant recommendation (or none yet resolved) keeps the prior
   * behavior instead of an unrecommended-looking slider.
   */
  recommendedPosture?: PhilosophyPosture
}

// R12 (AI-62) net-new Recovery-generosity body: a single overlay that replaces
// the 4-card StanceCardList with a 4-stop slider + ONE detail card for the
// selected stance (heading + RECOMMENDED FOR YOU badge + description + the
// shared Expect/Won't two-column detail). Controlled + pure: `value` +
// `onSelect` only — no chrome, no mutation. StanceCardList stays in-tree as the
// fallback. Per the R11→R12 image diff: no blue probabilistic banner and no
// "YOUR PHILOSOPHY" card wrapper. Slider spacing/colors are directional from
// the R12 Figma, pending design sign-off.
const GenerositySliderBody = ({
  value,
  onSelect,
  disabled = false,
  recommendedPosture = DEFAULT_RECOMMENDED_POSTURE,
}: Props) => {
  const [css] = useStyletron()
  const sliderRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  // indexOf is -1 for an unknown value; clamp to 0 so the slider always has a
  // valid stop.
  const selectedIndex = Math.max(0, SLIDER_ORDER.indexOf(value))
  const recommendedIndex = Math.max(0, SLIDER_ORDER.indexOf(recommendedPosture))

  const selectIndex = useCallback(
    (index: number) => {
      // Single choke point for every edit path (keyboard, drag, stop click), so
      // a read-only host can never emit onSelect no matter which one fires.
      if (disabled) return
      const clamped = Math.min(MAX_INDEX, Math.max(0, index))
      const next = SLIDER_ORDER[clamped]
      // Controlled + idempotent: only report a real change, so a clamped no-op
      // at either end doesn't fire onSelect.
      if (next !== value) onSelect(next)
    },
    [disabled, onSelect, value]
  )

  const selectFromPointer = useCallback(
    (clientX: number) => {
      const index = nearestStopIndex(
        clientX,
        sliderRef.current?.getBoundingClientRect()
      )
      if (index >= 0) selectIndex(index)
    },
    [selectIndex]
  )

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Read-only: no focus grab, no pointer capture, no drag state.
      if (disabled) return
      // Click-anywhere + start a drag. Focus the slider so keyboard works after,
      // and capture the pointer so a drag that leaves the track keeps tracking.
      sliderRef.current?.focus()
      try {
        sliderRef.current?.setPointerCapture(e.pointerId)
      } catch {
        // jsdom / unsupported — pointer capture is a progressive enhancement.
      }
      setIsDragging(true)
      selectFromPointer(e.clientX)
    },
    [disabled, selectFromPointer]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (isDragging) selectFromPointer(e.clientX)
    },
    [isDragging, selectFromPointer]
  )

  const endDrag = useCallback(() => setIsDragging(false), [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Read-only: don't even preventDefault, so arrow keys keep their native
      // page-scroll behaviour on a slider the merchant can't move.
      if (disabled) return
      switch (e.key) {
        // WAI-ARIA slider: Right/Up increase aria-valuenow (= more generous),
        // Left/Down decrease.
        case 'ArrowRight':
        case 'ArrowUp':
          e.preventDefault()
          selectIndex(selectedIndex + 1)
          break
        case 'ArrowLeft':
        case 'ArrowDown':
          e.preventDefault()
          selectIndex(selectedIndex - 1)
          break
        case 'Home':
          e.preventDefault()
          selectIndex(0)
          break
        case 'End':
          e.preventDefault()
          selectIndex(MAX_INDEX)
          break
      }
    },
    [disabled, selectIndex, selectedIndex]
  )

  const expectBullets = useMemo(
    () => expectBulletKeys(value).map(key => ({ id: key, text: t(key) })),
    [value]
  )
  const wontBullets = useMemo(
    () => wontBulletKeys(value).map(key => ({ id: key, text: t(key) })),
    [value]
  )

  const selectedPercent = stopPercent(selectedIndex)

  return (
    <div className={`d-flex flex-column ${css({ gap: '24px' })}`}>
      {/* Selected stance name — the focal heading of the overlay. */}
      <Text.Header3 bold>{t(value)}</Text.Header3>

      <div className={css({ position: 'relative', paddingTop: '28px' })}>
        {/* RECOMMENDED FOR YOU badge pinned above the recommended stop. At the
            two end stops the badge is edge-aligned with the track instead of
            centered, so it never overhangs the content column. */}
        <span
          className={css({
            position: 'absolute',
            top: 0,
            ...recommendedBadgePosition(recommendedIndex),
            ...recommendedBadgeStyle,
          })}
        >
          {t('recommended_for_you')}
        </span>

        {/* The slider control. role=slider + tabIndex own the keyboard contract;
            pointer handlers add click-anywhere + drag (the dots below stay
            clickable for discrete picks and carry the per-stop aria-labels). */}
        <div
          ref={sliderRef}
          role="slider"
          // Read-only slider stays FOCUSABLE (a disabled widget a keyboard user
          // can't reach is unreadable to AT); aria-disabled announces that it
          // can't be moved, and the handlers above no-op.
          tabIndex={0}
          aria-disabled={disabled}
          aria-label={t('slider_aria_label')}
          aria-orientation="horizontal"
          aria-valuemin={0}
          aria-valuemax={MAX_INDEX}
          aria-valuenow={selectedIndex}
          // Announce the recommended stop too — the visual badge's position
          // carries no semantics for AT (WCAG 1.3.1), so fold it into the value
          // text when the current stop is the recommended one.
          aria-valuetext={
            selectedIndex === recommendedIndex
              ? `${t(value)}, ${t('recommended_for_you')}`
              : t(value)
          }
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          className={css({
            position: 'relative',
            height: '24px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            outlineOffset: '4px',
            touchAction: 'none',
            userSelect: 'none',
          })}
        >
          {/* Track */}
          <div
            className={css({
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: TRACK_HEIGHT,
              transform: 'translateY(-50%)',
              backgroundColor: merchantTheme.colors.grey20,
              borderRadius: TRACK_RADIUS,
            })}
          />
          {/* Filled portion up to the selected stop. */}
          <div
            className={css({
              position: 'absolute',
              top: '50%',
              left: 0,
              width: `${selectedPercent}%`,
              height: TRACK_HEIGHT,
              transform: 'translateY(-50%)',
              backgroundColor: palette.belizeHole,
              borderRadius: TRACK_RADIUS,
            })}
          />
          {/* Stop hit-targets — MOUSE ONLY. Intentionally native <button>, NOT
              @thanx/uikit Button: these are slider thumb/track dots, not CTAs —
              a uikit Button would bake in kind-driven CTA chrome/sizing we'd
              fight via overrides for zero gain (same rationale as the sibling
              RewardMultiSelect's native trigger). tabIndex -1 keeps them out of
              the tab order; aria-hidden keeps them out of the AT tree entirely
              so the role=slider div is the SOLE assistive-tech interface (its
              aria-valuetext/valuenow + arrow keys own the contract) — WAI-ARIA
              treats slider as a leaf widget that shouldn't expose interactive
              descendants (a11y review 6Qlm). onClick still handles a discrete
              mouse pick; the aria-label is retained as a stable name for tests/
              tooling (AT-invisible while aria-hidden). */}
          {SLIDER_ORDER.map((posture, index) => (
            <button
              key={posture}
              type="button"
              tabIndex={-1}
              aria-hidden="true"
              aria-label={t(posture)}
              disabled={disabled}
              // Stable click hook: aria-hidden zeroes the computed accessible
              // name, so tests can't query these dots by role+name — they query
              // this testid instead.
              data-testid={`generosity-stop-${posture}`}
              onClick={() => selectIndex(index)}
              className={css({
                position: 'absolute',
                top: '50%',
                left: `${stopPercent(index)}%`,
                transform: 'translate(-50%, -50%)',
                width:
                  index === selectedIndex
                    ? STOP_SIZE_SELECTED
                    : STOP_SIZE_DEFAULT,
                height:
                  index === selectedIndex
                    ? STOP_SIZE_SELECTED
                    : STOP_SIZE_DEFAULT,
                borderRadius: '50%',
                border: `2px solid ${
                  index <= selectedIndex
                    ? palette.belizeHole
                    : merchantTheme.colors.grey40
                }`,
                backgroundColor: 'white',
                padding: 0,
                cursor: disabled ? 'not-allowed' : 'pointer',
              })}
            />
          ))}
        </div>
      </div>

      {/* Live region: the slider's aria-valuetext announces only the stance
          NAME on change, so wrap the supporting detail (description +
          Expect/Won't columns) in aria-live="polite" so screen-reader users
          also hear what changed beneath it after a slider move, without
          interrupting an in-progress announcement (a11y review 6Qln). */}
      <div
        className={`d-flex flex-column ${css({ gap: '24px' })}`}
        aria-live="polite"
      >
        {/* Selected stance description. */}
        <Text.BodyText3 color="grey70">{t(`${value}_desc`)}</Text.BodyText3>

        {/* Two-column do/don't detail card for the selected stance. Figma
            (node 2318:65821) specs this box as grey05 (#F8F9FA), but that's
            a ~1.02:1 contrast ratio against the page's white background —
            confirmed via pixel-sampling the Figma export itself, not just
            the app: the box is barely perceptible even in the design file.
            grey10 (#F2F4F5) is the accepted compromise — still a subtle
            wash, closer to Figma's intent than grey20, but with enough
            contrast (~1.06:1) to actually read as a container. */}
        <div
          className={`row ${css({
            margin: 0,
            backgroundColor: merchantTheme.colors.grey10,
            borderRadius: '4px',
            padding: '16px',
          })}`}
        >
          <div className="col-md-6">
            <Text.BodyText4 tag="h4" bold className="mb-xs">
              {t('more_likely_to')}
            </Text.BodyText4>
            <div className={`d-flex flex-column ${css({ gap: '8px' })}`}>
              {expectBullets.map(({ id, text }) => (
                <StanceBulletRow
                  key={id}
                  text={text}
                  iconClass="fa-check"
                  // AI-916: mint (spearmint50), matching RecoveryAI's established
                  // check-icon accent (ReasoningChecklist) — not the bright
                  // nephritis50 green the R12 slider shipped with.
                  iconColor={merchantTheme.colors.spearmint50}
                />
              ))}
            </div>
          </div>
          <div className="col-md-6">
            <Text.BodyText4 tag="h4" bold className="mb-xs">
              {t('less_likely_to')}
            </Text.BodyText4>
            <div className={`d-flex flex-column ${css({ gap: '8px' })}`}>
              {wontBullets.map(({ id, text }) => (
                <StanceBulletRow
                  key={id}
                  text={text}
                  iconClass="fa-minus"
                  iconColor={merchantTheme.colors.grey50}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GenerositySliderBody
